import { useState } from "react"; 
import { Link, NavLink, Outlet } from "react-router-dom"; 
import { Mail, Menu, Phone, X } from "lucide-react"; 
import { AnimatePresence, motion } from "framer-motion"; 
import { CONTACT_EMAIL, CONTACT_PHONE, CONTACT_PHONE_TEL, footerNavigation, navigation as siteNavigation } from "../lib/constants"; // ✅ Uses alias
import { useAuthBridge } from "../lib/auth"; 
import { useCMS } from "../hooks/useCMS"; 
import { Button, LinkButton } from "./Button"; 
import { Container } from "./Container"; 
import { Logo } from "./Logo"; 

interface LocalNavItem { 
  href: string; 
  label: string; 
} 

export function SiteShell() { 
  const [open, setOpen] = useState(false); 
  const { isSignedIn, role, signOut, configured } = useAuthBridge(); 
  const { data } = useCMS(); 
  const cms = data as { footer: { tagline: string } } | undefined; 
  const dashboardHref = role === "admin" ? "/admin" : "/dashboard"; 
  const close = () => setOpen(false); 

  const primaryNav = siteNavigation as readonly LocalNavItem[]; 
  const footerNavCombined = [...siteNavigation, ...footerNavigation] as readonly LocalNavItem[]; 
  return ( 
    <div className="min-h-screen"> 
      <a href="#main" className="skip-link"> Skip to content </a> 
      <header className="sticky top-0 z-50 border-b border-brand-line/80 bg-brand-background/95 backdrop-blur-xl"> 
        <Container className="flex h-16 items-center justify-between gap-4 lg:h-20"> 
          <Logo /> 
          <nav aria-label="Primary navigation" className="hidden items-center gap-6 lg:flex"> 
            {primaryNav.map((item) => ( 
              <NavLink 
                key={item.href} 
                to={item.href} 
                className={({ isActive }) => 
                  `focus-ring border-b-2 pb-1 text-sm font-black uppercase tracking-[0.08em] transition ${ 
                    isActive 
                      ? "border-brand-accent text-brand-text" 
                      : "border-transparent text-brand-text/64 hover:border-brand-text/20 hover:text-brand-text" 
                  }` 
                } 
              > 
                {item.label} 
              </NavLink> 
            ))} 
          </nav> 
          <div className="hidden items-center gap-3 lg:flex"> 
            {CONTACT_PHONE ? ( 
              <a className="focus-ring inline-flex items-center gap-2 rounded-md px-2 py-2 text-sm font-bold text-brand-text/72 hover:text-brand-text" href={`tel:${CONTACT_PHONE_TEL}`}> 
                <Phone size={14} aria-hidden="true" /> {CONTACT_PHONE} 
              </a> 
            ) : null} 
            <a className="focus-ring inline-flex items-center gap-2 rounded-md px-2 py-2 text-sm font-bold text-brand-text/72 hover:text-brand-text" href={`mailto:${CONTACT_EMAIL}`}> 
              <Mail size={14} aria-hidden="true" /> {CONTACT_EMAIL} 
            </a> 
            {configured && isSignedIn ? ( 
              <> 
                <LinkButton to={dashboardHref} variant="secondary" size="sm"> Dashboard </LinkButton> 
                <Button variant="ghost" size="sm" onClick={() => void signOut()}> Sign out </Button> 
              </> 
            ) : ( 
              <> 
                <LinkButton to="/login" variant="ghost" size="sm"> Log in </LinkButton> 
                <LinkButton to="/contact" variant="dark" size="sm" showArrow> Build </LinkButton> 
              </> 
            )} 
          </div> 
          <button 
            className="focus-ring inline-flex h-11 w-11 items-center justify-center rounded-brand border border-brand-line bg-white/70 lg:hidden" 
            aria-label={open ? "Close navigation" : "Open navigation"} 
            aria-expanded={open} 
            onClick={() => setOpen((value) => !value)} 
          > 
            {open ? <X aria-hidden="true" size={20} /> : <Menu aria-hidden="true" size={20} />} 
          </button> 
        </Container> 
        <AnimatePresence> 
          {open ? ( 
            <motion.div 
              initial={{ opacity: 0, height: 0 }} 
              animate={{ opacity: 1, height: "auto" }} 
              exit={{ opacity: 0, height: 0 }} 
              className="max-h-[calc(100vh-5rem)] overflow-y-auto border-t border-brand-line bg-brand-background/95 lg:hidden" 
            > 
              <Container className="space-y-2 py-5"> 
                {primaryNav.map((item) => ( 
                  <NavLink 
                    key={item.href} 
                    to={item.href} 
                    onClick={close} 
                    className={({ isActive }) => 
                      `focus-ring block rounded-brand border px-4 py-3 font-bold ${ 
                        isActive ? "border-brand-line bg-white text-brand-text shadow-sm" : "border-transparent text-brand-text/72" 
                      }` 
                    } 
                  > 
                    {item.label} 
                  </NavLink> 
                ))} 
                <div className="grid gap-3 border-t border-brand-line pt-4 text-sm font-bold text-brand-text/72"> 
                  {CONTACT_PHONE ? ( 
                    <a className="focus-ring inline-flex items-center gap-2 rounded-md px-1 py-1 hover:text-brand-text" href={`tel:${CONTACT_PHONE_TEL}`}> 
                      <Phone size={16} aria-hidden="true" /> {CONTACT_PHONE} 
                    </a> 
                  ) : null} 
                  <a className="focus-ring inline-flex items-center gap-2 rounded-md px-1 py-1 hover:text-brand-text" href={`mailto:${CONTACT_EMAIL}`}> 
                    <Mail size={16} aria-hidden="true" /> {CONTACT_EMAIL} 
                  </a> 
                </div> 
                <div className="grid gap-3 pt-1"> 
                  {configured && isSignedIn ? ( 
                    <LinkButton to={dashboardHref} variant="dark" onClick={close}> Dashboard </LinkButton> 
                  ) : ( 
                    <> 
                      <LinkButton to="/login" variant="secondary" onClick={close}> Log in </LinkButton> 
                      <LinkButton to="/contact" variant="dark" showArrow onClick={close}> Build </LinkButton> 
                    </> 
                  )} 
                </div> 
              </Container> 
            </motion.div> 
          ) : null} 
        </AnimatePresence> 
      </header> 
      <main id="main"> 
        <Outlet /> 
      </main> 
      <footer className="border-t border-brand-line bg-white/55"> 
        <Container className="grid gap-10 py-12 lg:grid-cols-[1.2fr_0.8fr_0.8fr]"> 
          <div> 
            <Logo /> 
            <p className="mt-5 max-w-md text-sm leading-7 text-brand-text/62">{cms?.footer.tagline}</p> 
            <div className="mt-5 grid gap-3 text-sm font-black"> 
              <a className="focus-ring inline-flex w-fit items-center gap-2 rounded-brand text-brand-accent" href={`mailto:${CONTACT_EMAIL}`}> 
                <Mail size={16} aria-hidden="true" /> {CONTACT_EMAIL} 
              </a> 
              {CONTACT_PHONE ? ( 
                <a className="focus-ring inline-flex w-fit items-center gap-2 rounded-brand text-brand-text/70" href={`tel:${CONTACT_PHONE_TEL}`}> 
                  <Phone size={16} aria-hidden="true" /> {CONTACT_PHONE} 
                </a> 
              ) : null} 
            </div> 
          </div> 
          <div> 
            <p className="text-sm font-black uppercase text-brand-text/45">Platform</p> 
            <div className="mt-4 grid gap-3 text-sm font-bold text-brand-text/68"> 
              {footerNavCombined.map((item) => ( 
                <Link className="focus-ring rounded-md hover:text-brand-text" key={item.href} to={item.href}> 
                  {item.label} 
                </Link> 
              ))} 
            </div> 
          </div> 
          <div> 
            <p className="text-sm font-black uppercase text-brand-text/45">Client tools</p> 
            <div className="mt-4 grid gap-3 text-sm font-bold text-brand-text/68"> 
              <Link className="focus-ring rounded-md hover:text-brand-text" to="/dashboard"> Client Dashboard </Link> 
              <Link className="focus-ring rounded-md hover:text-brand-text" to="/admin"> Admin Dashboard </Link> 
              <Link className="focus-ring rounded-md hover:text-brand-text" to="/login"> Login </Link> 
              <Link className="focus-ring rounded-md hover:text-brand-text" to="/signup"> Signup </Link> 
            </div> 
          </div> 
        </Container> 
        <Container className="flex flex-col gap-3 border-t border-brand-line py-6 text-sm text-brand-text/52 sm:flex-row sm:items-center sm:justify-between"> 
          <p>© {new Date().getFullYear()} Aladdin Shenewa. All rights reserved.</p> 
          <p>Canonical domain: aladdinshenewa.online</p> 
        </Container> 
      </footer> 
    </div> 
  ); 
}
